"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { usePathname } from "next/navigation";
import { withContactDetails } from "@/data/knowledge/company";
import { useSkipAnimations } from "@/lib/animations";
import { useTranslation } from "@/lib/i18n";
import type { ChatAction, ChatStreamEvent } from "./types";

// HETZNER_MIGRATION: Set NEXT_PUBLIC_CHAT_API_URL=https://api.haintz.dev/chat
const CHAT_API_URL = process.env.NEXT_PUBLIC_CHAT_API_URL || "/api/chat";

/** Mirrors MAX_HISTORY_LENGTH in src/app/api/chat/route.ts. */
const MAX_HISTORY = 20;
/** Mirrors MAX_MESSAGE_LENGTH in src/app/api/chat/route.ts. */
export const MAX_MESSAGE_LENGTH = 500;

const STORAGE_KEY = "haintz.assistant.conversation";
/*
 * Dismissal is remembered for the same lifetime as the conversation it
 * dismisses. Held only in React state it was forgotten on reload, so closing
 * the panel and coming back re-opened it — the retained conversation was enough
 * to bring the docked panel back, and the visitor's "no" had been thrown away.
 */
const DISMISSED_KEY = "haintz.assistant.dismissed";
/*
 * Groups this conversation's exchanges in the server-side record.
 *
 * Stored beside the conversation rather than held in React state, because the
 * conversation itself survives a reload: an in-memory identifier would split a
 * conversation the visitor experiences as continuous into two unrelated groups
 * in the record, which is precisely the correlation the recording exists for.
 *
 * It therefore shares the conversation's lifetime exactly — minted on the first
 * question, restored with the conversation, dropped when the conversation is
 * cleared, and gone when the tab closes. See docs/compliance/chat-aufzeichnung.md.
 */
const CONVERSATION_ID_KEY = "haintz.assistant.conversation-id";

export interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  isStreaming?: boolean;
  /**
   * Something the assistant offered on this turn — a prefilled contact form or
   * a destination. Rendered as a control the visitor activates; the assistant
   * never carries it out.
   */
  action?: ChatAction;
}

/**
 * The four appearances from the design, plus `hidden` — which now means only
 * "not decided yet", off the home page, before the stored conversation has been
 * read. An unconfigured provider no longer hides the assistant; it answers.
 *
 * `inline`      — Ask-Bar resting in the home page's flow (A)
 * `inline-open` — the same panel unfolded over the hero (B)
 * `docked`      — the same open panel, bottom-right, narrower (C)
 * `collapsed`   — the bubble, only after the visitor closes it (D)
 */
export type Appearance =
  | "hidden"
  | "inline"
  | "inline-open"
  | "docked"
  | "collapsed";

interface ConversationValue {
  messages: Message[];
  isStreaming: boolean;
  appearance: Appearance;
  /** True once the visitor has dismissed the assistant this session. */
  isDismissed: boolean;
  send: (text: string) => void;
  clear: () => void;
  open: () => void;
  close: () => void;
  /** The Ask-Bar's reservation slot registers itself here; null off the home page. */
  registerAnchor: (node: HTMLElement | null) => void;
  anchor: HTMLElement | null;
  /** Driven by the slot's IntersectionObserver in AssistantHost. */
  setAnchorVisible: (visible: boolean) => void;
}

const ConversationContext = createContext<ConversationValue | null>(null);

/*
 * True only once hydration is over.
 *
 * `getServerSnapshot` is what React uses for the server render AND the
 * hydrating client render, so both agree on `false`; the first render after
 * hydration sees `true`. That is the gate the stored conversation is read
 * behind — reading it any earlier makes the client's markup disagree with the
 * server's, which is React #418.
 */
const noopSubscribe = () => () => {};
export function useHydrated() {
  return useSyncExternalStore(
    noopSubscribe,
    () => true,
    () => false
  );
}

function readDismissed(): boolean {
  try {
    return sessionStorage.getItem(DISMISSED_KEY) === "1";
  } catch {
    return false;
  }
}

function rememberDismissed(dismissed: boolean) {
  try {
    if (dismissed) sessionStorage.setItem(DISMISSED_KEY, "1");
    else sessionStorage.removeItem(DISMISSED_KEY);
  } catch {
    // Storage unavailable — dismissal simply does not survive the reload.
  }
}

/**
 * The identifier for the conversation in progress, minting one if there is none.
 *
 * Read at send time rather than kept in state: the value only matters when a
 * request is about to carry it, and reading it there is what makes a restored
 * conversation keep the identifier it already had.
 */
function conversationId(): string | undefined {
  try {
    const existing = sessionStorage.getItem(CONVERSATION_ID_KEY);
    if (existing) return existing;
    const minted = crypto.randomUUID();
    sessionStorage.setItem(CONVERSATION_ID_KEY, minted);
    return minted;
  } catch {
    // Private mode or storage disabled. The exchange is still recorded; its
    // record simply says the conversation could not be grouped.
    return undefined;
  }
}

function forgetConversationId() {
  try {
    sessionStorage.removeItem(CONVERSATION_ID_KEY);
  } catch {
    // Storage unavailable — there was nothing to forget.
  }
}

function readStored(): Message[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (m): m is Message =>
          typeof m === "object" &&
          m !== null &&
          typeof (m as Message).content === "string" &&
          ((m as Message).role === "user" || (m as Message).role === "assistant")
      )
      .map((m) => ({ ...m, isStreaming: false }))
      .slice(-MAX_HISTORY);
  } catch {
    // Private mode, disabled storage, or corrupt payload — start empty.
    return [];
  }
}

export function ConversationProvider({ children }: { children: ReactNode }) {
  const { t, locale } = useTranslation();

  /*
   * Read in a mount effect, never during render. AssistantHost is now
   * server-rendered, so a render-time read diverges: the server has no
   * sessionStorage and renders the bubble, while the client's first render
   * already has the conversation and renders the docked panel — React #418.
   */
  const [messages, setMessages] = useState<Message[]>([]);
  const [restored, setRestored] = useState(false);

  const [isStreaming, setIsStreaming] = useState(false);
  /*
   * A page load never counts as "open", even with a conversation in
   * sessionStorage. Seeding this from stored messages meant reloading the home
   * page threw the panel open over the hero and dimmed the page behind it —
   * nobody asked for that, and it hid the Ask-Bar the visitor came back to.
   * The Ask-Bar is the resting state; the conversation is retained and one
   * click brings it back. Where there is no Ask-Bar to rest in, `appearance`
   * below still shows the retained conversation docked.
   */
  const [isOpen, setIsOpen] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [anchorVisible, setAnchorVisible] = useState(true);

  // Adjusting state during render rather than in an effect: React re-runs this
  // component immediately and commits only the corrected output, so the
  // conversation is present in the same commit it is read in.
  const isHydrated = useHydrated();
  if (isHydrated && !restored) {
    setRestored(true);
    const stored = readStored();
    if (stored.length > 0) setMessages(stored);
    if (readDismissed()) setIsDismissed(true);
  }

  /*
   * The slot registers itself from a callback ref, which does not run on the
   * server or before hydration — so until it does, `anchor` is null and the
   * appearance below would resolve to the corner bubble even on the home page.
   * That is what made the Ask-Bar arrive a second or more after the page did:
   * nothing could be rendered for it until JavaScript had run.
   *
   * The route already tells us whether a slot is coming. Trusting it lets the
   * resting Ask-Bar be server-rendered, and the real anchor takes over the
   * moment it registers.
   */
  const pathname = usePathname();
  const expectsSlot = pathname === "/";


  const abortRef = useRef<AbortController | null>(null);

  /*
   * Mirror to sessionStorage whenever the conversation changes — but never
   * before it has been read back.
   *
   * Guarded on `restored`, which is real state, rather than on a "have I run
   * once" ref. Under `reactStrictMode` React deliberately runs every effect
   * twice, so the ref was armed by the first run and the second wiped the very
   * conversation the hydration gate was about to restore. That only happens in
   * development, which is why a production-build test suite could not see it.
   */
  useEffect(() => {
    if (!restored) return;
    try {
      const persistable = messages
        .filter((m) => m.content.length > 0 || m.action)
        .slice(-MAX_HISTORY)
        .map(({ id, role, content, action }) => ({ id, role, content, action }));
      if (persistable.length === 0) sessionStorage.removeItem(STORAGE_KEY);
      else sessionStorage.setItem(STORAGE_KEY, JSON.stringify(persistable));
    } catch {
      // Storage unavailable — the conversation simply does not survive reload.
    }
  }, [messages, restored]);

  /*
   * Reveal pacing.
   *
   * The route enqueues every model chunk on its own, and curl sees ~294 SSE
   * events about 15 ms apart. The browser does not: its fetch reader coalesces
   * them into a handful of bursts, so appending each arriving chunk straight
   * into the message painted the answer in about six lurches of several words.
   *
   * So arriving text is buffered here and released a few characters per frame
   * instead. Smoothness then depends on our own clock rather than on how the
   * bytes happened to be packetised. The rate is proportional to the backlog —
   * a big buffer drains fast, the tail drains character by character — so this
   * paces the answer without falling behind it.
   */
  const pendingRef = useRef("");
  const revealTargetRef = useRef<string | null>(null);
  const streamDoneRef = useRef(false);
  const rafRef = useRef<number | null>(null);
  const skipAnimations = useSkipAnimations();

  const stopReveal = useCallback(() => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    pendingRef.current = "";
    revealTargetRef.current = null;
  }, []);

  // Held in a ref so the loop can schedule itself without referencing its own
  // binding, which the lint rule (rightly) refuses.
  const drainRef = useRef<() => void>(() => {});

  const drain = useCallback(() => {
    const id = revealTargetRef.current;
    if (!id) {
      rafRef.current = null;
      return;
    }

    const pending = pendingRef.current;
    if (pending.length > 0) {
      // Proportional to the backlog so the tail is smooth and a big buffer
      // still clears quickly — but capped, because without a ceiling a single
      // large burst is revealed in one frame and the lurch is back. 16 per
      // frame is ~960 characters a second, far quicker than anyone reads.
      const take = Math.min(16, Math.max(1, Math.ceil(pending.length / 6)));
      const slice = pending.slice(0, take);
      pendingRef.current = pending.slice(take);
      setMessages((prev) =>
        prev.map((m) => (m.id === id ? { ...m, content: m.content + slice } : m))
      );
    }

    if (pendingRef.current.length > 0 || !streamDoneRef.current) {
      rafRef.current = requestAnimationFrame(() => drainRef.current());
      return;
    }

    // Buffer empty and the response is closed: the answer is complete.
    rafRef.current = null;
    revealTargetRef.current = null;
    setMessages((prev) =>
      prev.map((m) => (m.id === id ? { ...m, isStreaming: false } : m))
    );
    setIsStreaming(false);
  }, []);

  useEffect(() => {
    drainRef.current = drain;
  }, [drain]);

  // Abort an in-flight request if the provider itself goes away.
  useEffect(
    () => () => {
      abortRef.current?.abort();
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    },
    []
  );

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim().slice(0, MAX_MESSAGE_LENGTH);
      if (!trimmed || isStreaming) return;

      setIsOpen(true);
      setIsDismissed(false);
      rememberDismissed(false);

      const userMsg: Message = {
        id: crypto.randomUUID(),
        role: "user",
        content: trimmed,
      };
      const assistantMsg: Message = {
        id: crypto.randomUUID(),
        role: "assistant",
        content: "",
        isStreaming: true,
      };

      const history = [
        ...messages.map(({ role, content }) => ({ role, content })),
        { role: "user" as const, content: trimmed },
      ].slice(-MAX_HISTORY);

      setMessages((prev) => [...prev, userMsg, assistantMsg]);

      setIsStreaming(true);

      // Arm the reveal buffer for this answer.
      pendingRef.current = "";
      revealTargetRef.current = assistantMsg.id;
      streamDoneRef.current = false;

      /*
       * Mark the answer finished.
       *
       * If text is still queued the drain loop keeps revealing it and clears
       * `isStreaming` when it runs out; otherwise this finishes here. Either
       * way the caret stays until the last character is on screen, which is
       * what "still being written" means to a reader. Safe to call twice.
       */
      const settle = () => {
        streamDoneRef.current = true;
        if (pendingRef.current.length > 0 && !skipAnimations) return;
        stopReveal();
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMsg.id ? { ...m, isStreaming: false } : m
          )
        );
        setIsStreaming(false);
      };

      try {
        abortRef.current = new AbortController();

        const response = await fetch(CHAT_API_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          // `pathname` is this origin's own route, and the server validates it
          // against the route registry before using it — see route.ts.
          body: JSON.stringify({
            messages: history,
            locale,
            page: pathname,
            conversationId: conversationId(),
          }),
          signal: abortRef.current.signal,
        });

        if (!response.ok) {
          if (response.status === 429) {
            // A minute-long cooldown and an exhausted daily quota both arrive
            // as 429; only the scope header says which, and they need
            // different advice.
            throw new Error(
              response.headers.get("X-RateLimit-Scope") === "day"
                ? t("chat.error.rateLimitDaily")
                : t("chat.error.rateLimit"),
            );
          }
          // Deliberately NOT surfacing the body's `error` string. The route's
          // failure text is a fixed English sentence, so showing it put
          // "Failed to process chat request." in front of a German visitor —
          // against specs/ai-assistant — "Failures are shown in the
          // conversation, not as page errors", which requires the visitor's
          // language. Every non-429 failure gets the localised generic message.
          throw new Error(t("chat.error.generic"));
        }
        if (!response.body) throw new Error(t("chat.error.generic"));

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";

          for (const line of lines) {
            const trimmedLine = line.trim();
            if (!trimmedLine.startsWith("data: ")) continue;

            try {
              const event: ChatStreamEvent = JSON.parse(trimmedLine.slice(6));
              if (event.type === "token" && event.content) {
                const chunk = event.content;
                if (skipAnimations) {
                  // Reduced motion: no paced reveal, just show the text.
                  setMessages((prev) =>
                    prev.map((m) =>
                      m.id === assistantMsg.id
                        ? { ...m, content: m.content + chunk }
                        : m
                    )
                  );
                } else {
                  pendingRef.current += chunk;
                  if (rafRef.current === null) {
                    rafRef.current = requestAnimationFrame(() =>
                      drainRef.current()
                    );
                  }
                }
              } else if (event.type === "answer_complete") {
                // The text is all here; only an action may still follow. Settle
                // the caret now so the visitor is not left watching one while
                // the server decides whether to offer them a control.
                settle();
              } else if (event.type === "action" && event.action) {
                // Attached to the answer it belongs to, so it survives the
                // reveal pacing and is persisted with the conversation.
                const offered = event.action;
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantMsg.id ? { ...m, action: offered } : m
                  )
                );
              } else if (event.type === "error") {
                throw new Error(event.error || t("chat.error.generic"));
              }
              // type === "done" — the loop ends when the reader closes.
              // Any other event type is ignored, so a client that predates a
              // new one keeps rendering a correct conversation.
            } catch (parseErr) {
              if (
                parseErr instanceof Error &&
                parseErr.message !== "Unexpected end of JSON input"
              ) {
                throw parseErr;
              }
            }
          }
        }

        // The response is closed. Settling again is harmless: whichever came
        // first — the answer_complete event or the body ending — has already
        // done it.
        settle();
      } catch (err) {
        // Reveal whatever had already been buffered, then stop pacing — an
        // error must not leave the caret spinning over a queue nobody drains.
        streamDoneRef.current = true;
        const buffered = pendingRef.current;
        stopReveal();

        // The visitor cancelled (closed, cleared, navigated). Nothing failed,
        // so nothing is said: just settle whatever text had arrived.
        const cancelled = err instanceof DOMException && err.name === "AbortError";

        /*
         * A failure is answered, not reported.
         *
         * The visitor asked a question and is owed a reply, so the assistant
         * says what happened and how else to reach a human — rather than a red
         * box that leaves them with nowhere to go. `chat.busy` covers the rate
         * limit, `chat.unavailable` everything else; both carry the address and
         * the phone number from `COMPANY`.
         */
        const isRateLimit =
          err instanceof Error && err.message === t("chat.error.rateLimit");
        const isDailyLimit =
          err instanceof Error &&
          err.message === t("chat.error.rateLimitDaily");
        const reply = cancelled
          ? ""
          : withContactDetails(
              isDailyLimit
                ? t("chat.quotaReached")
                : isRateLimit
                  ? t("chat.busy")
                  : t("chat.unavailable"),
            );

        setMessages((prev) =>
          prev
            .map((m) => {
              if (m.id !== assistantMsg.id) return m;
              const partial = m.content + buffered;
              // Keep any partial answer and add the note under it.
              const content = reply
                ? partial
                  ? `${partial}\n\n${reply}`
                  : reply
                : partial;
              return { ...m, content, isStreaming: false };
            })
            // Only a cancellation can leave the placeholder empty.
            .filter((m) => m.id !== assistantMsg.id || m.content)
        );
        setIsStreaming(false);
      } finally {
        abortRef.current = null;
      }
    },
    [isStreaming, locale, messages, pathname, skipAnimations, stopReveal, t]
  );

  const clear = useCallback(() => {
    abortRef.current?.abort();
    stopReveal();
    setIsStreaming(false);
    setMessages([]);
    setIsOpen(false);
    setIsDismissed(false);
    rememberDismissed(false);
    // The conversation is over, so its identifier is too. The next question
    // mints a fresh one and starts a new group in the record.
    forgetConversationId();
  }, [stopReveal]);

  const open = useCallback(() => {
    setIsDismissed(false);
    rememberDismissed(false);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    abortRef.current?.abort();
    stopReveal();
    setIsStreaming(false);
    setIsOpen(false);
    // Only consulted when no slot is on screen — see `appearance` above. With
    // the Ask-Bar visible the visitor lands back on it; scrolled away, they get
    // the bubble. Written to storage so a reload does not undo the decision.
    setIsDismissed(true);
    rememberDismissed(true);
  }, [stopReveal]);

  const registerAnchor = useCallback((node: HTMLElement | null) => {
    setAnchor(node);
    // A page with no slot is docked from the start.
    if (!node) setAnchorVisible(false);
    else setAnchorVisible(true);
  }, []);

  const appearance: Appearance = useMemo(() => {
    // The Ask-Bar is part of the page, not a feature of the model behind it:
    // it is shown whether or not a provider is configured or reachable. There
    // is deliberately nothing to gate it on — an unconfigured deployment fails
    // the request and `send` answers with the address and phone number, which
    // is the same thing the client used to pre-empt by asking
    // /api/chat/status on every page load.

    // Where the slot is on screen, the Ask-Bar IS the resting appearance —
    // whether or not the visitor has dismissed the panel, and whether or not a
    // conversation exists. Closing at the top of the home page therefore
    // returns to the Ask-Bar, not to a bubble sitting next to an empty gap.
    // The conversation is retained either way and reopens on click.
    if (anchor ? anchorVisible : expectsSlot) {
      return isOpen ? "inline-open" : "inline";
    }

    // Off the home page there is nothing to server-render: whether this is the
    // docked panel or the bubble depends on a conversation only the browser can
    // see. Render nothing until it has been read, rather than flashing a bubble
    // and replacing it a frame later.
    if (!restored) return "hidden";

    // No slot on this route, or the visitor has scrolled past it. Only here
    // does dismissal decide between the docked panel and the bubble.
    //
    // A retained conversation counts as open here even though a page load does
    // not set `isOpen`: with no Ask-Bar to rest in, the docked panel IS the
    // resting appearance, and collapsing to a bubble the visitor never asked
    // for would drop the conversation out of sight on reload and on every
    // navigation — see specs/ai-assistant, "Conversation survives navigation".
    return (isOpen || messages.length > 0) && !isDismissed
      ? "docked"
      : "collapsed";
  }, [
    anchor,
    anchorVisible,
    expectsSlot,
    isDismissed,
    isOpen,
    messages.length,
    restored,
  ]);

  const value = useMemo<ConversationValue>(
    () => ({
      messages,
      isStreaming,
      appearance,
      isDismissed,
      send,
      clear,
      open,
      close,
      registerAnchor,
      anchor,
      setAnchorVisible,
    }),
    [
      messages,
      isStreaming,
      appearance,
      isDismissed,
      send,
      clear,
      open,
      close,
      registerAnchor,
      anchor,
    ]
  );

  return (
    <ConversationContext.Provider value={value}>
      {children}
    </ConversationContext.Provider>
  );
}

export function useConversation() {
  const ctx = useContext(ConversationContext);
  if (!ctx) {
    throw new Error(
      "useConversation must be used within a ConversationProvider"
    );
  }
  return ctx;
}
